import { CalendarCheck, CalendarDays } from 'lucide-react';

import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { cn } from '@/lib/utils';
import type { ParentAttendanceStats, ParentAttendanceStatus } from '../../types/attendance';
import type { ParentAttendanceDay, ParentTimeframe } from '../../types/overview';
import { OverviewCard } from './OverviewCard';

export const TIMEFRAME_OPTIONS: Option<ParentTimeframe>[] = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
];

/**
 * The status family every attendance screen shares: present emerald, late
 * amber, absent rose, leave blue. Absent used to be grey and leave rose here,
 * which drew a missed day like an unmarked one and a granted leave like an
 * absence.
 */
const DAY_TONE: Record<ParentAttendanceStatus, string> = {
  PRESENT: 'bg-emerald-500 text-white font-bold shadow-xs',
  LATE: 'bg-amber-500 text-white font-bold shadow-xs',
  LEAVE: 'bg-blue-500 text-white font-bold shadow-xs',
  ABSENT: 'bg-rose-500 text-white font-bold shadow-xs',
  NOT_MARKED: 'border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500',
  HOLIDAY: 'border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500',
  WEEK_OFF: 'border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500',
  UPCOMING: 'border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500',
};

const KIND_LABEL: Record<ParentAttendanceStatus, string> = {
  PRESENT: 'Present',
  LATE: 'Late',
  LEAVE: 'On leave',
  ABSENT: 'Absent',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'Week off',
  NOT_MARKED: 'Not marked',
  UPCOMING: 'Still to come',
};

export interface ChildAttendanceProps {
  /** The counts for the chosen window — the Attendance page's own arithmetic. */
  attendance: ParentAttendanceStats;
  /** The seven days up to the date being read, oldest first. */
  days: ParentAttendanceDay[];
  timeframe: ParentTimeframe;
  onTimeframeChange: (timeframe: ParentTimeframe) => void;
  /** The seven days the strip covers, written out — "28 Aug - 3 Sep 2026". */
  rangeLabel: string;
  childName: string;
}

export function ChildAttendance({
  attendance,
  days,
  timeframe,
  onTimeframeChange,
  rangeLabel,
  childName,
}: ChildAttendanceProps) {
  const percent = attendance.overallPercent;

  // SVG Gauge calculations
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const displayPercent = percent ?? 0;
  const strokeDashoffset = circumference - (displayPercent / 100) * circumference;

  return (
    <OverviewCard
      title="Attendance"
      icon={<CalendarCheck className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />}
      tone="ok"
      action={
        <Select
          label={`Choose the period counted for ${childName}`}
          hideLabel
          hideSearch
          options={TIMEFRAME_OPTIONS}
          value={timeframe}
          onChange={(event) => onTimeframeChange(event.target.value as ParentTimeframe)}
          className="h-8 w-36 px-2.5 text-xs font-semibold rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
        />
      }
    >
      <div className="flex flex-col gap-4">
        {/* Top: 7-day strip card */}
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3.5 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-900 dark:text-white">Last 7 Days</p>
            <p className="text-sm font-medium tabular-nums text-slate-500 dark:text-slate-400">{rangeLabel}</p>
          </div>

          <ul className="flex items-center gap-2 sm:gap-2.5">
            {days.map((day) => (
              <li key={day.date} className="flex-1">
                <span
                  title={`${day.weekday} — ${day.holidayName ?? KIND_LABEL[day.status]}`}
                  className={cn(
                    'grid h-9 w-full place-items-center rounded-xl text-xs transition-colors',
                    DAY_TONE[day.status],
                  )}
                >
                  <span aria-hidden className="text-base">
                    {day.weekday.charAt(0)}
                  </span>
                  <span className="sr-only text-base">
                    {day.weekday}: {day.holidayName ?? KIND_LABEL[day.status]}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Working days counter */}
        <p className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">
          <CalendarDays className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400" aria-hidden />
          <span>
            Days the register was taken:{' '}
            <strong className="font-bold text-slate-900 dark:text-white">
              {attendance.markedDays} {attendance.markedDays === 1 ? 'Day' : 'Days'}
            </strong>
          </span>
        </p>

        {/* Bottom Section: Stat Grid (Left) and Gauge (Right) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* 4 Stat Boxes */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:col-span-8 gap-2.5">
            <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3 text-center">
              <span className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                PRESENT
              </span>
              <span className="mt-1 block text-2xl font-black tabular-nums text-emerald-500">
                {attendance.presentDays}
              </span>
            </div>

            <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3 text-center">
              <span className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                LATE
              </span>
              <span className="mt-1 block text-2xl font-black tabular-nums text-amber-500">
                {attendance.lateDays}
              </span>
            </div>

            <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3 text-center">
              <span className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                LEAVE
              </span>
              <span className="mt-1 block text-2xl font-black tabular-nums text-blue-500">
                {attendance.leaveDays}
              </span>
            </div>

            <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3 text-center">
              <span className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                ABSENT
              </span>
              <span className="mt-1 block text-2xl font-black tabular-nums text-rose-500">
                {attendance.absentDays}
              </span>
            </div>
          </div>

          {/* Attendance Circular Gauge */}
          <div className="flex flex-col items-center justify-center md:col-span-4">
            <div className="relative flex h-28 w-28 sm:h-32 sm:w-32 items-center justify-center">
              <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r={radius}
                  className="stroke-slate-200/80 dark:stroke-slate-800"
                  strokeWidth="8"
                  fill="transparent"
                />
                <circle
                  cx="50"
                  cy="50"
                  r={radius}
                  className="stroke-emerald-500 transition-all duration-700 ease-out"
                  strokeWidth="8"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  fill="transparent"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <span className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tabular-nums leading-none">
                  {percent === null ? '—' : `${percent}%`}
                </span>
                <span className="mt-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 tracking-wider">
                  ATTENDANCE
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OverviewCard>
  );
}
